import { useMemo, useState } from "react";
import { Modal, Input, TextArea, Select, ListBox, Spinner, toast } from "@heroui/react";
import { Button } from "src/components/button";
import { modalApi } from "src/infra/modals";
import { useAppApi } from "src/appApi";
import type { Key } from "react-aria-components";

export type CreditNoteInvoiceOption = {
	orderId: string;
	docNumber: string;
	docUuid: string;
	date: number; // epoch millis
};

type Props = {
	organizationId: string;
	organizationName: string;
	invoiceOptions: CreditNoteInvoiceOption[];
	onCreated?: () => void;
};

const NO_INVOICE = "none";

const CREDIT_REASONS = [
	"מוצר פגום",
	"אריזה פגומה",
	"תאריך תפוגה קצר",
	"טעות בהזמנה",
	"החזרת מוצר",
	"אחר",
] as const;

const ERROR_MESSAGES: Record<string, string> = {
	invalid_input: "נתונים לא תקינים. בדוק את הטופס ונסה שוב.",
	organization_missing: "הארגון לא נמצא.",
	invoice_missing: "לא נמצאה חשבונית מקושרת",
	organization_mismatch: "החשבונית שנבחרה אינה שייכת לארגון זה",
	ezcount_failed: "יצירת המסמך ב-EZcount נכשלה — נסה שוב",
	ar_failed:
		"המסמך הופק אך עדכון היתרה נכשל — לחץ שוב על הפק זיכוי בלי לסגור את החלון (לא ייווצר מסמך כפול)",
};

const NETWORK_ERROR_MESSAGE =
	"שגיאת תקשורת — לא ידוע אם הזיכוי הופק. לחץ שוב על הפק זיכוי (אותו מפתח ימנע כפילות), ואם התקלה חוזרת בדוק בכרטסת לפני ניסיון חדש.";

/** Today as YYYY-MM-DD in Israel timezone. */
function todayIsrael(): string {
	return Intl.DateTimeFormat("en-CA", {
		timeZone: "Asia/Jerusalem",
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
	}).format(new Date());
}

function fmtDate(ms: number): string {
	return new Date(ms).toLocaleDateString("he-IL");
}

export function CreateCreditNoteModal({
	organizationId,
	organizationName,
	invoiceOptions,
	onCreated,
}: Props) {
	const appApi = useAppApi();

	// Generated once per modal open so retries after network errors are idempotent.
	const idempotencyKey = useMemo(() => `crd-${crypto.randomUUID()}`, []);

	const [selectedOrderId, setSelectedOrderId] = useState<string>(NO_INVOICE);
	const [creditDate, setCreditDate] = useState(todayIsrael());
	const [amount, setAmount] = useState<string>("");
	const [reason, setReason] = useState<string>(CREDIT_REASONS[0]);
	const [note, setNote] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [inlineError, setInlineError] = useState<string | null>(null);

	// Mirrors the backend sanity cap (createCreditNote.ts) — 1,000,000 ILS.
	const MAX_AMOUNT_ILS = 1_000_000;

	const parsedAmount = Number(amount);
	const isAmountValid =
		amount.trim() !== "" &&
		Number.isFinite(parsedAmount) &&
		parsedAmount > 0 &&
		parsedAmount <= MAX_AMOUNT_ILS;
	const canSubmit = isAmountValid && !!creditDate && !!reason;

	const handleSubmit = async () => {
		if (!canSubmit) return;

		setInlineError(null);
		setSubmitting(true);

		const trimmedNote = note.trim();

		try {
			const result = await appApi.admin.createCreditNote({
				organizationId,
				orderId: selectedOrderId === NO_INVOICE ? null : selectedOrderId,
				amount: Math.round(parsedAmount * 100),
				reason,
				...(trimmedNote ? { note: trimmedNote } : {}),
				creditDate: new Date(creditDate).getTime(),
				idempotencyKey,
			});

			if (!result) {
				setInlineError("שגיאה בלתי צפויה. נסה שוב.");
				return;
			}

			if (result.success) {
				toast.success(`תעודת זיכוי ${result.creditNote.doc_number} הופקה`);
				onCreated?.();
				modalApi.closeModal("createCreditNote");
				return;
			}

			setInlineError(ERROR_MESSAGES[result.code] ?? result.error);
		} catch {
			// Callable rejected outright (network drop, server internal error) — we
			// genuinely don't know if the credit note was issued. Keep the same
			// idempotencyKey so retrying from this same modal can't double-issue it.
			setInlineError(NETWORK_ERROR_MESSAGE);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Modal.Backdrop
			isOpen
			onOpenChange={(open) => {
				if (!open && !submitting) modalApi.closeModal("createCreditNote");
			}}
		>
			<Modal.Container size="md" scroll="inside">
				<Modal.Dialog>
					<Modal.Header>
						<Modal.Heading>
							<div className="text-start" dir="rtl">
								הפקת זיכוי — {organizationName}
							</div>
						</Modal.Heading>
					</Modal.Header>

					<Modal.Body>
						<div className="space-y-5" dir="rtl">
							{/* Linked invoice */}
							<div className="flex flex-col gap-1">
								<label className="text-sm font-medium">חשבונית קשורה</label>
								<Select
									selectedKey={selectedOrderId}
									onSelectionChange={(k: Key | null) => {
										if (k) setSelectedOrderId(String(k));
									}}
									aria-label="חשבונית קשורה"
								>
									<Select.Trigger>
										<Select.Value />
										<Select.Indicator />
									</Select.Trigger>
									<Select.Popover>
										<ListBox>
											<ListBox.Item id={NO_INVOICE} textValue="ללא קישור לחשבונית">
												ללא קישור לחשבונית
											</ListBox.Item>
											{invoiceOptions.map((inv) => (
												<ListBox.Item
													key={inv.orderId}
													id={inv.orderId}
													textValue={`${inv.docNumber} — ${fmtDate(inv.date)}`}
												>
													{inv.docNumber} — {fmtDate(inv.date)}
												</ListBox.Item>
											))}
										</ListBox>
									</Select.Popover>
								</Select>
							</div>

							{/* Date + amount row */}
							<div className="grid grid-cols-2 gap-3">
								<div className="flex flex-col gap-1">
									<label className="text-sm font-medium">
										תאריך <span className="text-[var(--danger)]">*</span>
									</label>
									<Input
										type="date"
										value={creditDate}
										onChange={(e) => setCreditDate(e.target.value)}
										aria-label="תאריך"
									/>
								</div>

								<div className="flex flex-col gap-1">
									<label className="text-sm font-medium">
										סכום זיכוי (₪) <span className="text-[var(--danger)]">*</span>
									</label>
									<Input
										type="number"
										step="0.01"
										min="0"
										max={MAX_AMOUNT_ILS}
										inputMode="decimal"
										value={amount}
										onChange={(e) => setAmount(e.target.value)}
										aria-label="סכום זיכוי"
										placeholder="0.00"
									/>
								</div>
							</div>

							{/* Reason */}
							<div className="flex flex-col gap-1">
								<label className="text-sm font-medium">סיבת זיכוי</label>
								<Select
									selectedKey={reason}
									onSelectionChange={(k: Key | null) => {
										if (k) setReason(String(k));
									}}
									aria-label="סיבת זיכוי"
								>
									<Select.Trigger>
										<Select.Value />
										<Select.Indicator />
									</Select.Trigger>
									<Select.Popover>
										<ListBox>
											{CREDIT_REASONS.map((r) => (
												<ListBox.Item key={r} id={r} textValue={r}>
													{r}
												</ListBox.Item>
											))}
										</ListBox>
									</Select.Popover>
								</Select>
							</div>

							{/* Note */}
							<div className="flex flex-col gap-1">
								<label className="text-sm font-medium">הערות (אופציונלי)</label>
								<TextArea
									placeholder="פרטים נוספים..."
									value={note}
									onChange={(e) => setNote(e.target.value)}
									rows={3}
									aria-label="הערות"
								/>
							</div>

							{/* Inline error */}
							{inlineError && (
								<p className="text-sm text-[var(--danger)] font-medium">{inlineError}</p>
							)}
						</div>
					</Modal.Body>

					<Modal.Footer>
						{submitting && <Spinner size="sm" className="me-2" />}
						<Button
							variant="ghost"
							onPress={() => modalApi.closeModal("createCreditNote")}
							isDisabled={submitting}
						>
							ביטול
						</Button>
						<Button
							variant="primary"
							onPress={handleSubmit}
							isPending={submitting}
							isDisabled={!canSubmit}
						>
							הפק זיכוי
						</Button>
					</Modal.Footer>
				</Modal.Dialog>
			</Modal.Container>
		</Modal.Backdrop>
	);
}
